/** One scroll per improvement, without moving the selection or focusing either editor. */
export function createSummaryChangeScroll(getElement: () => HTMLElement | null | undefined) {
  let used = false;
  let frame: number | undefined;
  let removeListeners: (() => void) | undefined;
  const stop = () => {
    used = true;
    if (frame !== undefined) cancelAnimationFrame(frame);
    frame = undefined;
    removeListeners?.(); removeListeners = undefined;
  };
  return {
    cancel: stop,
    reveal() {
      if (used || frame !== undefined) return;
      frame = requestAnimationFrame(() => {
        frame = undefined;
        const element = getElement();
        if (!element) return;
        const marker = element.querySelector<HTMLElement>(".ai-change");
        const scroller = element.closest<HTMLElement>("main");
        if (!marker?.getClientRects().length || !scroller) return;
        used = true;
        const bounds = scroller.getBoundingClientRect();
        const top = bounds.top + (scroller.querySelector(".app-page-header")?.getBoundingClientRect().height ?? 0);
        const markerBounds = marker.getBoundingClientRect();
        // A change that is already readable needs no camera movement.
        if (markerBounds.top >= top + 24 && markerBounds.bottom <= bounds.bottom - 24) return;
        let from = scroller.scrollTop;
        let to = Math.max(0, Math.min(scroller.scrollHeight - scroller.clientHeight,
          from + markerBounds.top - top - Math.min(80, Math.max(24, (bounds.bottom - top) / 4))));
        if (Math.abs(to - from) < 1) return;
        if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) { scroller.scrollTop = to; return; }
        // Explicit frames keep streaming DOM/scroll-anchor corrections from cancelling
        // a browser-native smooth scroll halfway through. User input always wins.
        const events = ["wheel", "touchstart", "pointerdown", "keydown"] as const;
        events.forEach(event => scroller.addEventListener(event, stop, { passive: true }));
        removeListeners = () => events.forEach(event => scroller.removeEventListener(event, stop));
        const started = performance.now();
        let previousTop = from;
        const animate = (now: number) => {
          frame = undefined;
          if (!scroller.isConnected || !getElement()) { stop(); return; }
          // Preserve any reading-position correction caused by the next snapshot.
          const adjustment = scroller.scrollTop - previousTop;
          from += adjustment; to += adjustment;
          const progress = Math.min(1, Math.max(0, (now - started) / 420));
          scroller.scrollTop = from + (to - from) * (1 - (1 - progress) ** 3);
          previousTop = scroller.scrollTop;
          if (progress < 1) frame = requestAnimationFrame(animate);
          else stop();
        };
        frame = requestAnimationFrame(animate);
      });
    },
  };
}
